/**
 * The Messages tab of a topic: newest first (partition, offset, time, key, value, which groups consumed it), the
 * latest N or N from an offset, one partition or all, a search in the loaded page; a row opens its value (pretty JSON
 * or raw) and headers. The messages are read whatever the app's consumer groups committed, as long as Kafka keeps them.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import DataTable from '../table/DataTable';
import { ArrowClockwise, Check } from '@phosphor-icons/react';
import { EmptyState, ErrorState, Loading, errorText } from './DataCommon';
import { getTopicMessages } from '../../services/DataService';
import { ago, formatCount } from '../../utils/dataModel';
import { consumedByOf, isJson, matches, newestFirst, prettyValue, shortValue } from './kafkaModel';

const LIMITS = [20, 50, 100, 200, 500];

function Consumed({ groups, known }) {
  if (!known.length) { return <span className="data-muted">no group</span>; }
  if (!groups.length) { return <span className="data-pill is-warn" title={`Not consumed yet by ${known.join(', ')}`}>to consume</span>; }
  return (
    <span className="data-kafka-badges">
      {groups.map((g) => <span className="data-pill is-ok" key={g} title={`Consumed by ${g} (below its committed offset)`}><Check aria-hidden size={11} />{g}</span>)}
    </span>
  );
}

Consumed.propTypes = { groups: PropTypes.arrayOf(PropTypes.string).isRequired, known: PropTypes.arrayOf(PropTypes.string).isRequired };

function MessageDetail({ message }) {
  const json = isJson(message);
  const [pretty, setPretty] = useState(json);
  const headers = Object.entries(message.headers || {});
  return (
    <div className="data-kafka-detail">
      <div className="data-actions">
        {json && (
          <div aria-label="Value view" className="data-seg" role="group">
            <button aria-pressed={pretty} onClick={() => setPretty(true)} type="button">Pretty</button>
            <button aria-pressed={!pretty} onClick={() => setPretty(false)} type="button">Raw</button>
          </div>
        )}
        {message.encoding === 'base64' && <span className="data-pill">binary, shown as base64</span>}
        <span className="data-muted">{message.timestamp ? new Date(message.timestamp).toISOString() : 'no timestamp'}</span>
      </div>
      <pre className="data-text-preview data-mono">{pretty ? prettyValue(message) : String(message.value ?? '')}</pre>
      {headers.length > 0 && (
        <dl className="data-dl" aria-label="Headers">
          {headers.map(([k, v]) => (
            <React.Fragment key={k}><dt className="data-mono">{k}</dt><dd className="data-mono">{v ?? ''}</dd></React.Fragment>
          ))}
        </dl>
      )}
    </div>
  );
}

MessageDetail.propTypes = { message: PropTypes.object.isRequired };

export default function KafkaMessages({ projectId, topic, consumers = null, canWrite = false, refreshKey = 0 }) {
  const [partition, setPartition] = useState('');
  const [mode, setMode] = useState('latest');
  const [from, setFrom] = useState('0');
  const [fromInput, setFromInput] = useState('0');
  const [limit, setLimit] = useState(50);
  const [search, setSearch] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);
  // The topic as the calls name it: stable while the list refreshes (a new object every few seconds).
  const ref = useMemo(() => ({ name: topic.name, topic: topic.topic }), [topic.name, topic.topic]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setResult(await getTopicMessages(projectId, ref, {
        limit, partition: partition === '' ? undefined : partition, from: mode === 'from' ? Math.max(0, Number(from) || 0) : undefined,
      }));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setLoading(false);
    }
  }, [projectId, ref, limit, partition, mode, from]);

  useEffect(() => { setResult(null); setOpen(null); load(); }, [load]); // eslint-disable-line react-hooks/exhaustive-deps
  // New messages (the topic's end moved): read again, in "latest" mode only (a page from an offset stays put).
  const lastKey = useRef(refreshKey);
  useEffect(() => {
    if (lastKey.current === refreshKey) { return; }
    lastKey.current = refreshKey;
    if (mode === 'latest') { load(); }
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const known = useMemo(() => (consumers?.groups ? consumers.groups.map((g) => g.groupId) : result?.groups || []), [consumers, result]);
  const shown = useMemo(() => newestFirst((result?.messages || []).filter((m) => matches(m, search))), [result, search]);
  const partitions = Number(topic?.partitions) || 0;
  const ends = result?.partitions || [];
  const removed = ends.reduce((n, p) => n + p.low, 0);

  return (
    <section aria-labelledby="data-messages-title" className="data-card">
      <div className="data-card-head">
        <h2 id="data-messages-title">Messages</h2>
        <span className="data-grow" />
        <label className="data-label" htmlFor="data-partition">Partition</label>
        <select className="data-input" id="data-partition" onChange={(e) => setPartition(e.target.value)} value={partition}>
          <option value="">All</option>
          {Array.from({ length: partitions }, (_, i) => <option key={i} value={String(i)}>{i}</option>)}
        </select>
        <div aria-label="Which messages" className="data-seg" role="group">
          <button aria-pressed={mode === 'latest'} onClick={() => setMode('latest')} type="button">Latest</button>
          <button aria-pressed={mode === 'from'} onClick={() => setMode('from')} type="button">From offset</button>
        </div>
        {mode === 'from' && (
          <>
            <label className="data-sr" htmlFor="data-from">From offset</label>
            <input
              className="data-input is-narrow data-mono"
              id="data-from"
              inputMode="numeric"
              min={0}
              onBlur={() => setFrom(fromInput)}
              onChange={(e) => setFromInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setFrom(fromInput); } }}
              type="number"
              value={fromInput}
            />
          </>
        )}
        <label className="data-label" htmlFor="data-limit">Show</label>
        <select className="data-input" id="data-limit" onChange={(e) => setLimit(Number(e.target.value))} value={limit}>
          {LIMITS.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <button aria-label="Refresh messages" className="data-btn" disabled={loading} onClick={load} type="button"><ArrowClockwise aria-hidden className={loading ? 'data-spin' : undefined} size={15} /></button>
      </div>
      <div className="data-toolbar">
        <label className="data-sr" htmlFor="data-search">Search the loaded messages</label>
        <input className="data-input data-grow" id="data-search" onChange={(e) => setSearch(e.target.value)} placeholder="Search key, value or headers in this page" type="search" value={search} />
        {result && <span className="data-muted" role="status">{search ? `${shown.length} of ${result.messages.length}` : `${formatCount(result.messages.length)} loaded`}</span>}
      </div>
      {result?.partial && <p className="data-banner" role="status">Kafka was slow to answer: this page may miss some messages. Refresh to read again.</p>}
      {error && <ErrorState error={error} onRetry={load} />}
      {!error && !result && <Loading label="Loading the messages…" />}
      {result && !result.messages.length && (
        <EmptyState title={removed > 0 ? 'No messages left in the log' : 'No messages yet'}>
          {removed > 0
            ? `Kafka already removed the ${formatCount(removed)} older messages (retention). New ones show here as they arrive.`
            : canWrite ? 'Produce one in the Produce tab, or start a service that sends some.' : null}
        </EmptyState>
      )}
      {result && result.messages.length > 0 && !shown.length && <EmptyState title="Nothing matches">No loaded message contains “{search}”.</EmptyState>}
      {shown.length > 0 && (
        <div className="data-table-wrap">
          <DataTable
            after={(m, { colCount }) => (open === `${m.partition}:${m.offset}` ? (
              <tr className="is-detail">
                <td colSpan={colCount}><MessageDetail message={m} /></td>
              </tr>
            ) : null)}
            className="data-table"
            columns={[
              { key: 'partition', label: 'Partition', width: 90, className: 'data-mono', value: (m) => m.partition },
              { key: 'offset', label: 'Offset', width: 100, className: 'data-mono', value: (m) => Number(m.offset) },
              {
                key: 'time',
                label: 'Time',
                width: 120,
                value: (m) => (m.timestamp ? Number(m.timestamp) || Date.parse(m.timestamp) : null),
                title: (m) => (m.timestamp ? new Date(Number(m.timestamp) || m.timestamp).toISOString() : undefined),
                render: (m) => (m.timestamp ? ago(Number(m.timestamp) || m.timestamp) : ''),
              },
              { key: 'key', label: 'Key', width: 160, className: 'data-mono', value: (m) => m.key, render: (m) => m.key ?? <span className="data-muted">none</span> },
              {
                key: 'value',
                label: 'Value',
                className: 'data-mono data-value-cell',
                value: (m) => shortValue(m),
                title: (m) => shortValue(m),
                render: (m) => {
                  const id = `${m.partition}:${m.offset}`;
                  const expanded = open === id;
                  return <button aria-expanded={expanded} className="data-link" onClick={() => setOpen(expanded ? null : id)} type="button">{shortValue(m) || '(empty)'}</button>;
                },
              },
              { key: 'consumed', label: 'Consumed', width: 180, value: (m) => consumedByOf(m, consumers).length, render: (m) => <Consumed groups={consumedByOf(m, consumers)} known={known} /> },
            ]}
            rowKey={(m) => `${m.partition}:${m.offset}`}
            rowProps={(m) => ({ className: open === `${m.partition}:${m.offset}` ? 'is-selected' : '', 'data-testid': 'kafka-message' })}
            rows={shown}
            storageKey="flow.data.kafkaMessages.hiddenColumns"
          />
        </div>
      )}
    </section>
  );
}

KafkaMessages.propTypes = {
  canWrite: PropTypes.bool,
  consumers: PropTypes.object,
  projectId: PropTypes.string.isRequired,
  refreshKey: PropTypes.number,
  topic: PropTypes.object.isRequired,
};
